import React from "react";
import { ShieldCheck, BadgeCheck, FileText, CalendarCheck, Building2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const CERTIFICATE_PDF = "/certificates/CFS-Zertifikat-MP-Carrosserie-2026.pdf";

export default function Certification() {
  const { t } = useI18n();
  const c = t.certification;

  return (
    <section
      id="certification"
      data-testid="certification-section"
      className="relative py-20 md:py-28 bg-[#050505] border-y border-white/5 overflow-hidden"
    >
      {/* subtle grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      {/* red glow */}
      <div
        aria-hidden
        className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-[#e52620]/10 blur-[120px] pointer-events-none"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT: Headline & description */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#e52620]" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 font-bold font-mono">
                {c.eyebrow}
              </span>
            </div>
            <h2
              data-testid="certification-title"
              className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95] text-balance"
            >
              {c.title}
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-md">
              {c.subtitle}
            </p>

            <a
              href={CERTIFICATE_PDF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="certification-view-pdf-btn"
              className="mt-8 inline-flex items-center gap-3 bg-[#e52620] hover:bg-[#ff2e26] text-white font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-md transition-colors duration-300"
            >
              <FileText className="w-4 h-4" strokeWidth={2.5} />
              {c.cta}
            </a>
          </div>

          {/* RIGHT: Certificate card */}
          <div className="lg:col-span-7">
            <div
              data-testid="certification-card"
              className="relative bg-gradient-to-br from-[#0e0e0e] to-[#080808] border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl"
            >
              {/* Decorative shield */}
              <div className="absolute -top-6 -left-6 w-14 h-14 rounded-xl bg-[#e52620] flex items-center justify-center shadow-lg shadow-[#e52620]/30 ring-4 ring-[#050505]">
                <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.25} />
              </div>

              {/* Issuer line */}
              <div className="flex items-center justify-between mb-8 pt-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                    {c.issuer_label}
                  </div>
                  <div className="mt-1 text-white font-bold text-lg tracking-tight">
                    {c.issuer}
                  </div>
                </div>
                <BadgeCheck className="w-8 h-8 text-[#e52620]" strokeWidth={2} />
              </div>

              {/* Standards grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                {c.standards.map((s, i) => (
                  <div
                    key={i}
                    data-testid={`certification-standard-${i}`}
                    className="bg-black/40 border border-white/10 rounded-lg p-4 hover:border-[#e52620]/40 transition-colors duration-300"
                  >
                    <div className="text-white font-black text-base tracking-tight font-mono">
                      {s.code}
                    </div>
                    <div className="mt-1 text-zinc-400 text-xs leading-snug">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Scope */}
              <div className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">
                  {c.scope_label}
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  {c.scope}
                </p>
              </div>

              {/* Dates row */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                <div className="flex items-start gap-3">
                  <CalendarCheck className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                      {c.issued_label}
                    </div>
                    <div className="text-white text-sm font-bold mt-0.5">
                      {c.issued}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" strokeWidth={2} />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
                      {c.valid_label}
                    </div>
                    <div className="text-white text-sm font-bold mt-0.5">
                      {c.valid}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
